import type { ChatButton } from "../types/ChatButton";
import type { ChatCardProps } from "../types/ChatCard";
import type { Attachment } from "../types/attachment";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import {
  formatConfirmationCard,
  formatLinkCard,
  formatListingCard,
  formatPreviewCard,
} from "./cardFormatters";
import { convertHtmlToText } from "./htmlToText";
import { extractCards } from "./messageUtils";

const formatAttachments = (attachments: Attachment[]): string => {
  if (!attachments || attachments.length === 0) return "";
  const fileList = attachments.map(a => `- ${a.filename}`).join("\n");
  return `[Attachments]\n${fileList}`;
};

const formatButtons = (
  buttons: ChatButton[],
  selectedButtonEvent: string | null,
): string => {
  if (!buttons || buttons.length === 0) return "";
  return `[Options]\n${buttons
    .map(b => {
      let text = b.label;
      if (b.sublabel) text += ` (${b.sublabel})`;
      if (selectedButtonEvent === b.event) text += " [SELECTED]";
      return `- ${text}`;
    })
    .join("\n")}`;
};

export const formatCardAsText = (card: ChatCardProps): string => {
  const { type, content } = card;

  switch (type) {
    case "preview_card":
      return formatPreviewCard(content as any);

    case "confirmation_card":
      return formatConfirmationCard(content as any);

    case "link_card":
      return formatLinkCard(content as any);

    case "member_listing_card":
    case "invitation_link_listing_card":
    case "member_tag_listing_card":
    case "profile_field_listing_card":
    case "space_group_listing_card":
    case "space_listing_card":
    case "access_group_listing_card":
    case "community_segment_listing_card":
      return formatListingCard(content as any, type);

    default:
      return "";
  }
};

const formatCards = (cards: ChatCardProps[]): string => {
  if (!cards || cards.length === 0) return "";

  return cards.map(formatCardAsText).filter(Boolean).join("\n\n");
};

export const formatMessageAsText = (message: HelpWidgetMessage): string => {
  const contentParts = [convertHtmlToText(message.content)];

  if (message.attachments && message.attachments.length > 0) {
    contentParts.push(formatAttachments(message.attachments));
  }

  if (message.buttons && message.buttons.length > 0) {
    contentParts.push(formatButtons(message.buttons, message.selected_button));
  }

  const extractedCards = extractCards(message) as ChatCardProps[];
  if (extractedCards && extractedCards.length > 0) {
    contentParts.push(formatCards(extractedCards));
  }

  return contentParts.filter(Boolean).join("\n\n");
};
